import * as THREE from 'three';

/**
 * A coarse walkable grid, used only to hand the AI the waypoint chains the bomb
 * mode needs.
 *
 * This is deliberately NOT a navmesh and not a per-frame pathfinder. The bots
 * still move with `_moveToward` + `_steer` reactive avoidance; measurement
 * showed that alone wedges a bot against Dust II's geometry at 0.74 m/s against
 * a 4.3 m/s run speed, so it never crosses the map. The plan called for
 * hand-authored `bombRoutes` per team per site — this generates chains of the
 * same shape and feeds the same interface, so no map needs authoring and a new
 * map gets routes for free.
 *
 * **Multi-layer, and that is not optional.** A single top-down sample per column
 * describes the roofs of a city level, not its streets: on Al-Rasheed that made
 * 97% of cells "walkable" while the goal's flood fill reached 48 of 14884 of
 * them, because every rooftop was its own island. Each column therefore keeps
 * every standable surface in it (street, catwalk, roof), and the graph node is
 * a (column, layer) pair.
 *
 * Built once per level, off a goal-keyed flow field so every bot heading to a
 * site shares one search.
 */

const UP = new THREE.Vector3(0, 1, 0);
const DOWN = new THREE.Vector3(0, -1, 0);
const _p = new THREE.Vector3();
const _d = new THREE.Vector3();

/**
 * Surfaces kept per column, counted from the ground up.
 *
 * Keeping the *topmost* surfaces instead is a trap: on a map with awnings and
 * overhangs the ground floor falls off the end of the list, and the walkable
 * graph shatters into roof-sized islands.
 */
const MAXL = 5;
/** Surfaces sampled per column before the lowest MAXL are kept. */
const SCAN = 12;
/**
 * Height a bot can gain between adjacent cells. Above the player's 0.55 step
 * because `_integrate` gives a blocked bot a 5.4 m/s hop, and because a stair
 * flight sampled at this pitch rises more per cell than one tread.
 */
const STEP = 1.1;
/**
 * Standing room above a surface for it to count as floor.
 *
 * Sized to the player (1.72 m), NOT to the Enemy capsule's 1.96 m, on
 * measurement: raising it to 2.05 to model the bot honestly dropped plants from
 * 25-30% to 5-20% over 20 rounds. Shaving it the other way is worse still —
 * 1.2 m admitted gaps the capsule cannot pass and produced 590 repaths in one
 * round. 1.7 m is where it measures best; the mismatch with bot height is real
 * but is evidently not what limits them.
 */
const HEAD = 1.7;

export class NavGrid {
  /**
   * @param {object} physics  needs `raycast(origin, dir, far)`
   * @param {THREE.Box3} box  world extents to cover
   * @param {number} cell     grid pitch in metres
   */
  constructor(physics, box, cell = 2.0) {
    this.physics = physics;
    this.cell = cell;
    this.min = box.min.clone();
    const size = box.getSize(new THREE.Vector3());
    this.nx = Math.max(1, Math.ceil(size.x / cell));
    this.nz = Math.max(1, Math.ceil(size.z / cell));

    const cols = this.nx * this.nz;
    this.cols = cols;
    this.layers = new Uint8Array(cols);          // surfaces found per column
    this.blocked = new Uint8Array(cols * MAXL);  // learned-impassable nodes
    this.floor = new Float32Array(cols * MAXL);  // node -> world y

    const top = box.max.y + 3;
    const bottom = box.min.y - 1;
    const o = new THREE.Vector3();
    const scan = new Float64Array(SCAN);

    for (let iz = 0; iz < this.nz; iz++) {
      for (let ix = 0; ix < this.nx; ix++) {
        const col = iz * this.nx + ix;
        const x = this.min.x + (ix + 0.5) * cell;
        const z = this.min.z + (iz + 0.5) * cell;

        // Walk the whole column top-down, collecting every standable surface.
        let y = top;
        let found = 0;
        while (found < SCAN && y > bottom) {
          o.set(x, y, z);
          const hit = physics.raycast(o, DOWN, y - bottom);
          if (!hit) break;
          const hy = hit.point.y;
          if (hit.normal.y >= 0.55) {
            o.set(x, hy + 0.4, z);
            // Head clearance: anything lower is a crawlspace the capsule sweep
            // would refuse anyway.
            if (!physics.raycast(o, UP, HEAD)) scan[found++] = hy;
          }
          // Drop below this surface and keep looking. The nudge has to clear
          // slab thickness or the same face is hit forever.
          y = hy - 0.25;
        }
        // Keep the lowest MAXL — ground before roofs. `scan` is descending.
        const keep = Math.min(found, MAXL);
        for (let k = 0; k < keep; k++) {
          this.floor[col * MAXL + k] = scan[found - 1 - k];
        }
        this.layers[col] = keep;
      }
    }

    this._buildEdges(physics);
    this._fields = new Map();
    this._label = null;
  }

  /**
   * Connected-component labels over the node graph, computed on demand.
   *
   * A level is not one walkable blob: Crossfire's bunker joins its surface only
   * by ladder, which a bot cannot climb, so the two are genuinely separate to a
   * walker. Anything that must be mutually reachable — spawns, bomb sites —
   * belongs in the largest component, and this is how that gets checked rather
   * than assumed.
   */
  components() {
    if (this._label) return this._label;
    const n = this.cols * MAXL;
    const label = new Int32Array(n).fill(-1);
    const sizes = [];
    const queue = new Int32Array(n);

    for (let seed = 0; seed < n; seed++) {
      const col = (seed / MAXL) | 0;
      if (seed % MAXL >= this.layers[col] || label[seed] !== -1) continue;
      if (this.blocked[seed]) continue;
      const id = sizes.length;
      let head = 0;
      let tail = 0;
      queue[tail++] = seed;
      label[seed] = id;
      let size = 0;
      while (head < tail) {
        const node = queue[head++];
        size++;
        this._forEachNeighbour(node, (j) => {
          if (label[j] !== -1) return;
          label[j] = id;
          queue[tail++] = j;
        });
      }
      sizes.push(size);
    }

    let main = 0;
    for (let i = 1; i < sizes.length; i++) if (sizes[i] > sizes[main]) main = i;
    this._label = { label, sizes, main };
    return this._label;
  }

  /**
   * Learn that a spot is impassable and forget every cached path.
   *
   * The grid is a sampled approximation; it will occasionally offer a gap the
   * capsule cannot actually squeeze through, and a bot that trusts it walks in,
   * backs out and walks straight back in forever. When steering reports that,
   * the honest answer is to believe the bot over the grid. Bounded so a bad
   * round cannot erode the level into nothing.
   */
  block(v, radius = 1.6) {
    if ((this._blocks ?? 0) > 60) return false;
    const r = Math.ceil(radius / this.cell);
    const cx = Math.floor((v.x - this.min.x) / this.cell);
    const cz = Math.floor((v.z - this.min.z) / this.cell);
    let n = 0;
    for (let dz = -r; dz <= r; dz++) {
      for (let dx = -r; dx <= r; dx++) {
        const ix = cx + dx;
        const iz = cz + dz;
        if (ix < 0 || iz < 0 || ix >= this.nx || iz >= this.nz) continue;
        const col = iz * this.nx + ix;
        for (let l = 0; l < this.layers[col]; l++) {
          const node = col * MAXL + l;
          if (this.blocked[node] || Math.abs(this.floor[node] - v.y) > 2.5) continue;
          this.blocked[node] = 1;
          n++;
        }
      }
    }
    if (!n) return false;
    this._blocks = (this._blocks ?? 0) + n;
    this._fields.clear();
    this._label = null;
    return true;
  }

  /**
   * Push a waypoint off the walls around it.
   *
   * Cell centres sit on a grid that knows nothing about the bot's width, so a
   * chain through a corridor can run right along one wall — and a bot that
   * hugs a wall wedges on the first corner. Four probes and a nudge toward the
   * open side costs nothing and keeps the path down the middle.
   */
  _centreInCorridor(q, reach = 1.8) {
    const o = _p.set(q.x, q.y + 0.9, q.z);
    let sx = 0;
    let sz = 0;
    for (let i = 0; i < 4; i++) {
      const dx = i === 0 ? 1 : i === 1 ? -1 : 0;
      const dz = i === 2 ? 1 : i === 3 ? -1 : 0;
      const hit = this.physics.raycast(o, _d.set(dx, 0, dz), reach);
      if (!hit) continue;
      // Push away, harder the closer the wall is.
      const push = (reach - hit.distance) / reach;
      sx -= dx * push;
      sz -= dz * push;
    }
    if (sx || sz) {
      const len = Math.hypot(sx, sz);
      const step = Math.min(0.6, len * 0.6);
      q.x += (sx / len) * step;
      q.z += (sz / len) * step;
    }
    return q;
  }

  /**
   * Forget everything learned. Called at round start: blocks that accumulate
   * unbounded across rounds will eventually wall a site off entirely, and the
   * result is chaotic — the same build measured 40% plants and then 0%.
   */
  clearBlocks() {
    if (!this._blocks) return;
    this.blocked.fill(0);
    this._blocks = 0;
    this._fields.clear();
    this._label = null;
  }

  /** Is this world point on the level's main walkable body? */
  inMain(v) {
    const node = this._nearest(v);
    if (node < 0) return false;
    const c = this.components();
    return c.label[node] === c.main;
  }

  /** Shared graph adjacency, so BFS and component labelling cannot diverge. */
  _forEachNeighbour(node, fn) {
    const col = (node / MAXL) | 0;
    const ix = col % this.nx;
    const iz = (col / this.nx) | 0;
    const y = this.floor[node];
    const mask = this.edges[node];
    let bit = -1;
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (!dx && !dz) continue;
        bit++;
        if (!(mask & (1 << bit))) continue;
        const jx = ix + dx;
        const jz = iz + dz;
        if (jx < 0 || jz < 0 || jx >= this.nx || jz >= this.nz) continue;
        const jcol = jz * this.nx + jx;
        // No cutting corners diagonally through a wall.
        if (dx && dz) {
          if (this._layerAt(iz * this.nx + jx, y, STEP) < 0
            || this._layerAt(jz * this.nx + ix, y, STEP) < 0) continue;
        }
        for (let l = 0; l < this.layers[jcol]; l++) {
          const j = jcol * MAXL + l;
          if (this.blocked[j]) continue;
          if (Math.abs(this.floor[j] - y) > STEP) continue;
          fn(j);
        }
      }
    }
  }

  /**
   * Which of the eight neighbours each node can actually be walked to.
   *
   * Adjacency by height difference alone is not enough: at this pitch a cell
   * lands on TOP of a wall, and a 1.1 m step then bridges the two sides of it.
   * A bot handed that route walks into the wall forever — measured on Dust II
   * as a carrier pinned at one x coordinate for a third of the round while its
   * waypoint sat a metre lower on the far side. So probe it: a chest-height ray
   * from each cell to each neighbour, once, at build time.
   */
  _buildEdges(physics) {
    const n = this.cols * MAXL;
    this.edges = new Uint8Array(n);
    const a = new THREE.Vector3();
    const dir = new THREE.Vector3();

    for (let col = 0; col < this.cols; col++) {
      const ix = col % this.nx;
      const iz = (col / this.nx) | 0;
      for (let l = 0; l < this.layers[col]; l++) {
        const node = col * MAXL + l;
        this._centre(node, a);
        a.y += 0.9;
        let mask = 0;
        let bit = 0;
        for (let dz = -1; dz <= 1; dz++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (!dx && !dz) continue;
            const b = bit++;
            const jx = ix + dx;
            const jz = iz + dz;
            if (jx < 0 || jz < 0 || jx >= this.nx || jz >= this.nz) continue;
            const reach = this.cell * (dx && dz ? 1.5 : 1.06);
            dir.set(dx, 0, dz).normalize();
            if (physics.raycast(a, dir, reach)) continue;   // wall in the way
            mask |= 1 << b;
          }
        }
        this.edges[node] = mask;
      }
    }
  }

  /**
   * Every walkable node on the level's main body, as world positions.
   *
   * Exists so deathmatch can spawn across the whole map. The authored
   * `spawnPoints` are useless for that on the BSP maps: Dust II's forty are two
   * huddles of twenty, one in each team's start room 89 m apart and nothing in
   * between, because they were placed for a mode with two sides. A free-for-all
   * wants the whole map, and the walkable grid already knows what that is.
   *
   * Off-body nodes are excluded, so nothing spawns on an unreachable ledge or
   * inside the geometry the flood fill never got to.
   */
  walkablePoints(stride = 1) {
    const c = this.components();
    const out = [];
    for (let col = 0; col < this.cols; col++) {
      for (let l = 0; l < this.layers[col]; l++) {
        const node = col * MAXL + l;
        if (this.blocked[node]) continue;
        if (c.label[node] !== c.main) continue;
        if (stride > 1 && (out.length * 0 + col) % stride !== 0) continue;
        out.push(this._centre(node, new THREE.Vector3()));
      }
    }
    return out;
  }

  /**
   * Barriers worth fighting from behind, derived from the grid.
   *
   * ## Why this exists
   *
   * `coverPoints` is authored data: `Level.js` pushes one per crate and
   * barrier on the procedural maps, and `CrossfireLevel` reads the BSP's own
   * pickup entities, which stand where players contest. Both work. Neither
   * covers the converted Counter-Strike maps, because those maps have no such
   * entities — measured in the payloads, `dust2.json` carries 0 POI and
   * `assault.json` carries 4, all of them the hostages in one room, against
   * Crossfire's 137. So `_acquireCover` always failed on Dust II and the
   * `suppress` and `flank` roles behaved as if the state did not exist, on half
   * the map pool.
   *
   * ## What a barrier looks like from in here
   *
   * `edges` already holds the answer and it cost eight raycasts per node at
   * build time: a bit per neighbour, set only if a chest-height ray reaches it.
   * A node with every bit set is open floor. A node with a FEW bits clear is
   * standing next to something at chest height — a crate, a pillar, a low
   * wall, the corner of a building. That is the shape worth emitting.
   *
   * Two to five blocked directions of eight. Below two is open ground, and
   * above five is a dead end or a doorway the bot would wedge in rather than
   * fight from.
   *
   * The point emitted is the BARRIER, not the standing spot, because that is
   * what a cover point means to `_acquireCover` — it places the bot 0.75 m
   * behind one, on the far side from its foe. So the node centre is pushed
   * into the blocked arc.
   *
   * Thinned to `spacing` so a long wall contributes a few slots and not forty:
   * every candidate costs `_acquireCover` a raycast, and a hundred points is
   * already more than eight bots can stand behind.
   */
  coverPoints(spacing = 4.5, limit = 160) {
    const c = this.components();
    const out = [];
    const taken = new Set();                 // spacing cells already filled
    const key = (x, z) => `${Math.round(x / spacing)},${Math.round(z / spacing)}`;
    const cand = [];

    for (let col = 0; col < this.cols; col++) {
      const ix = col % this.nx;
      const iz = (col / this.nx) | 0;
      for (let l = 0; l < this.layers[col]; l++) {
        const node = col * MAXL + l;
        if (this.blocked[node] || c.label[node] !== c.main) continue;
        const mask = this.edges[node];
        let blocked = 0;
        let bx = 0;
        let bz = 0;
        let bit = -1;
        for (let dz = -1; dz <= 1; dz++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (!dx && !dz) continue;
            bit++;
            // Off the edge of the grid is not a barrier; it is the end of the
            // world, and a point out there is cover from nothing.
            const jx = ix + dx;
            const jz = iz + dz;
            if (jx < 0 || jz < 0 || jx >= this.nx || jz >= this.nz) continue;
            if (mask & (1 << bit)) continue;
            blocked++;
            const len = dx && dz ? Math.SQRT1_2 : 1;
            bx += dx * len;
            bz += dz * len;
          }
        }
        if (blocked < 2 || blocked > 5) continue;
        const arc = Math.hypot(bx, bz);
        // A node blocked on opposite sides (a corridor) has no net direction
        // and no side to hide behind.
        if (arc < 0.5) continue;
        cand.push({ node, blocked, nx: bx / arc, nz: bz / arc });
      }
    }

    // Tightest pockets first, so the thinning keeps corners over flat wall.
    cand.sort((a, b) => b.blocked - a.blocked);
    const p = new THREE.Vector3();
    for (const k of cand) {
      if (out.length >= limit) break;
      this._centre(k.node, p);
      const x = p.x + k.nx * (this.cell * 0.9);
      const z = p.z + k.nz * (this.cell * 0.9);
      const cellKey = key(x, z);
      if (taken.has(cellKey)) continue;
      taken.add(cellKey);
      /*
       * FLOOR height, matching the authored points.
       *
       * `_cover` measures the gap to the standing spot as
       * `root.position.distanceTo(stand)`, and `root.position` is at the bot's
       * feet. `stand` inherits the cover point's y, so emitting these at chest
       * height would add a permanent 0.9 m to that distance and eat almost the
       * whole 1.0 m arrival tolerance. The probes in `_acquireCover` lift
       * themselves to chest height instead, which is where that belongs.
       */
      out.push(new THREE.Vector3(x, p.y, z));
    }
    return out;
  }

  get walkableCount() {
    let n = 0;
    for (let c = 0; c < this.cols; c++) n += this.layers[c];
    return n;
  }

  /** Column containing a world point, or -1 if outside the grid. */
  _col(v) {
    const ix = Math.floor((v.x - this.min.x) / this.cell);
    const iz = Math.floor((v.z - this.min.z) / this.cell);
    if (ix < 0 || iz < 0 || ix >= this.nx || iz >= this.nz) return -1;
    return iz * this.nx + ix;
  }

  /** Best node in a column for a point at height `y`, or -1. */
  _layerAt(col, y, tol = Infinity) {
    let best = -1;
    let bestD = tol;
    for (let l = 0; l < this.layers[col]; l++) {
      const node = col * MAXL + l;
      if (this.blocked[node]) continue;
      const d = Math.abs(this.floor[node] - y);
      if (d < bestD) { bestD = d; best = node; }
    }
    return best;
  }

  _centre(node, out = new THREE.Vector3()) {
    const col = (node / MAXL) | 0;
    const ix = col % this.nx;
    const iz = (col / this.nx) | 0;
    return out.set(
      this.min.x + (ix + 0.5) * this.cell,
      this.floor[node],
      this.min.z + (iz + 0.5) * this.cell,
    );
  }

  /**
   * Nearest node to a world point, searching outward in rings. With
   * `mainOnly`, ignores nodes off the level's main walkable body — that is what
   * stops a bot being handed a goal on the far side of a ladder.
   */
  _nearest(v, maxRings = 14, mainOnly = false) {
    const c = mainOnly ? this.components() : null;
    const ok = (node) => !c || c.label[node] === c.main;
    const c0 = this._col(v);
    if (c0 >= 0) {
      const n = this._layerAt(c0, v.y, 3.0);
      if (n >= 0 && ok(n)) return n;
    }
    const cx = Math.floor((v.x - this.min.x) / this.cell);
    const cz = Math.floor((v.z - this.min.z) / this.cell);
    for (let r = 1; r <= maxRings; r++) {
      let best = -1;
      let bestD = Infinity;
      for (let dz = -r; dz <= r; dz++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dz)) !== r) continue;
          const ix = cx + dx;
          const iz = cz + dz;
          if (ix < 0 || iz < 0 || ix >= this.nx || iz >= this.nz) continue;
          const col = iz * this.nx + ix;
          const node = this._layerAt(col, v.y);
          if (node < 0 || !ok(node)) continue;
          // Height agreement matters more than a metre of ground distance —
          // otherwise a point on the street snaps to the roof above it.
          const d = (dx * dx + dz * dz) * this.cell
            + Math.abs(this.floor[node] - v.y) * 6;
          if (d < bestD) { bestD = d; best = node; }
        }
      }
      if (best >= 0) return best;
    }
    return -1;
  }

  /**
   * BFS out from a goal node, producing a came-from field every bot heading
   * there can reuse. Uniform cost is fine at this pitch — we want "a way
   * through", not the shortest metre.
   */
  _field(goal) {
    let f = this._fields.get(goal);
    if (f) return f;

    const n = this.cols * MAXL;
    const from = new Int32Array(n).fill(-1);
    const seen = new Uint8Array(n);
    const queue = new Int32Array(n);
    let head = 0;
    let tail = 0;
    queue[tail++] = goal;
    seen[goal] = 1;

    while (head < tail) {
      const node = queue[head++];
      this._forEachNeighbour(node, (j) => {
        if (seen[j]) return;
        seen[j] = 1;
        from[j] = node;
        queue[tail++] = j;
      });
    }

    f = { from, seen };
    this._fields.set(goal, f);
    return f;
  }

  /** True if a straight walk between two nodes stays on connected ground. */
  _clear(a, b) {
    const ca = (a / MAXL) | 0;
    const cb = (b / MAXL) | 0;
    const ax = ca % this.nx;
    const az = (ca / this.nx) | 0;
    const bx = cb % this.nx;
    const bz = (cb / this.nx) | 0;
    const steps = Math.max(Math.abs(bx - ax), Math.abs(bz - az)) * 2;
    if (steps === 0) return true;
    let y = this.floor[a];
    for (let s = 1; s <= steps; s++) {
      const t = s / steps;
      const ix = Math.round(ax + (bx - ax) * t);
      const iz = Math.round(az + (bz - az) * t);
      const node = this._layerAt(iz * this.nx + ix, y, STEP);
      if (node < 0) return false;
      y = this.floor[node];
    }
    // And it has to actually arrive at b's surface, not one above or below it.
    return Math.abs(y - this.floor[b]) <= STEP;
  }

  /**
   * A waypoint chain from `start` to `goal`, or null if there is no route.
   * Collapsed to the few corners that matter — the plan's "4-6 points from
   * spawn to site" — because the steering handles everything between them. A
   * single waypoint is a valid answer: it means the goal is a straight walk.
   */
  route(start, goal) {
    let gi = this._nearest(goal);
    let si = this._nearest(start);
    if (gi < 0 || si < 0) return null;
    if (gi === si) return [goal.clone()];

    let { from, seen } = this._field(gi);
    if (!seen[si]) {
      // The pair straddles a break in the walkable world (a ladder, a drop).
      // Retry on the main body: better to walk to the closest reachable point
      // than to stand still, which is what a null route makes a bot do.
      gi = this._nearest(goal, 14, true);
      si = this._nearest(start, 14, true);
      if (gi < 0 || si < 0) return null;
      if (gi === si) return [this._centre(gi)];
      ({ from, seen } = this._field(gi));
      if (!seen[si]) return null;
    }

    const nodes = [];
    const limit = this.cols * MAXL;
    for (let i = si; i !== -1 && i !== gi; i = from[i]) {
      nodes.push(i);
      if (nodes.length > limit) return null;          // paranoia
    }
    nodes.push(gi);

    /*
     * Sample the corridor at a fixed spacing rather than string-pulling to
     * corners.
     *
     * String-pulling produces the shortest chain, and that is the wrong goal
     * here: it hands the bot 40 m straight-line spans, and reactive steering
     * cannot hold a line that long through Dust II's tunnels — measured as four
     * bots circling their spawn for a whole round with valid routes in hand.
     * Waypoints a few metres apart follow the corridor instead of cutting it.
     */
    const spacing = Math.max(4, this.cell * 3.5);
    const picked = [];
    let acc = Infinity;
    const c = new THREE.Vector3();
    const prev = new THREE.Vector3();
    for (let k = 0; k < nodes.length; k++) {
      this._centre(nodes[k], c);
      acc += k ? c.distanceTo(prev) : 0;
      prev.copy(c);
      if (acc >= spacing) { picked.push(nodes[k]); acc = 0; }
    }
    const last = nodes[nodes.length - 1];
    if (picked[picked.length - 1] !== last) picked.push(last);
    if (!picked.length) return [goal.clone()];

    const pts = picked.map((i) => this._centre(i)).map((q) => this._centreInCorridor(q));
    // Hand back the real goal as the last step, but only when the chain
    // actually ends there — otherwise keep the reachable node we settled for.
    if (this._centre(gi).distanceTo(goal) < this.cell * 2) {
      pts[pts.length - 1] = goal.clone();
    }
    return pts;
  }
}
